import type { ScrollViewInstance, ViewInstance } from 'react-native';
import React, { useMemo, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    Platform,
    TouchableOpacity,
    Linking,
    Alert,
    ActivityIndicator,
    useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialIcons } from '@expo/vector-icons';

import type {
    PaperActionConfig,
    PaperDetailViewProps,
} from './types';
import PaperMetadata from './PaperMetadata';
import PaperAbstract from './PaperAbstract';
import PaperActions from './PaperActions';
import PaperCliPromo from './PaperCliPromo';
import DigestContextBanner from './DigestContextBanner';
import PaperDigestContext from './PaperDigestContext';
import ProjectPaperFeedbackPanel from './ProjectPaperFeedbackPanel';
import SummaryView from './SummaryView';
import SummaryStatusIndicator from './SummaryStatusIndicator';
import CreateFeedLikeThisCard from '../digest/CreateFeedLikeThisCard';
import Header from '../Header';
import Footer from '../Footer';
import Button from '../ui/Button';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { useAuth } from '../../lib/AuthContext';
import { useSession } from '../../lib/SessionContext';
import { canAccessMainApp } from '../../utils/authHelpers';
import { getVersionedUrl } from '../../lib/paperUrl';
import { getPaperVenueLabel } from '../../lib/sourceHelpers';
import { trackFunnelEvent } from '../../lib/funnelEvents';
import { useUnpaywallCheck } from '../../hooks/useUnpaywallCheck';
import { usePubmedPdfAvailability } from '../../hooks/usePubmedPdfAvailability';
import { canGenerateSummaryFromPersistedPdf } from '../../lib/pdfAvailability';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import {
    buildSummaryLimitMessage,
    shouldBlockProjectCreation,
    shouldBlockSummaryGeneration,
    shouldPromptSummaryTrialActivation,
} from '../../lib/entitlements';
import { getLegacyUpgradeAction, isLegacyAccessEnded } from '../../lib/legacyTransition';
import {
    projectCreationSubscriptionMessage,
    projectCreationTrialLabel,
    requiresSubscriptionForProjectCreation,
} from '../../lib/projectCreationEntitlements';
import {
    buildBillingPreviewTrialHref,
    classifyBillingPreviewDigestAccess,
    classifyBillingPreviewProjectAccess,
    getBillingPreviewState,
} from '../../lib/billingPreviewState';
import PreviewTrackingCard from '../billing/PreviewTrackingCard';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useOptionalResolvedMessage } from '../../lib/i18n/messages';
import { useLingui } from '@lingui/react';

function normalizeSummaryState(
    value: PaperDetailViewProps['summaryStatus']
): 'not_requested' | 'queued' | 'processing' | 'failed' | 'available' | 'loading' {
    if (value === 'completed' || value === 'available') {
        return 'available';
    }

    if (value === 'queued' || value === 'processing' || value === 'failed' || value === 'not_requested' || value === 'loading') {
        return value;
    }

    return 'not_requested';
}

export default function PaperDetailView({
    paper,
    pageState,
    projectPaperContext,
    digestEntryContext,
    showBackLink = false,
    backLinkHref,
    backLinkText = '← Back',
    summary = null,
    summaryJob,
    summaryStatus = 'not_requested',
    summaryError = null,
    summaryLimitReached = false,
    onGenerateSummary,
    onRetry,
    onSharePress,
    feedbackPending = false,
    onSetProjectFeedback,
    onClearProjectFeedback,
}: PaperDetailViewProps) {
    const router = useRouter();
    const backNavigationInFlightRef = useRef(false);
    const scrollViewRef = useRef<ScrollViewInstance>(null);
    const aiSummarySectionRef = useRef<ViewInstance>(null);
    const { fontsLoaded } = useFontLoading();
    const { openAuth } = useAuth();
    const { session } = useSession();
    const { width } = useWindowDimensions();
    const { i18n } = useLingui();
    const resolvedSummaryError = useOptionalResolvedMessage(summaryError);

    const resolvedSummaryState = normalizeSummaryState(summaryStatus);
    const detectedVenueLabel = getPaperVenueLabel({
        sourceId: paper.source_id,
        sourceName: paper.source?.name,
        sourcePaperId: paper.source_paper_id,
        url: paper.url,
    });
    const venueLabel = paper.venue_name || detectedVenueLabel;
    const isPubmed = detectedVenueLabel === 'PubMed';
    const unpaywallAccess = useUnpaywallCheck(paper.doi, Boolean(paper.pdf_url), !isPubmed);
    const pubmedPdfAvailability = usePubmedPdfAvailability({
        paper,
        isPubmed,
        accessToken: session?.access_token,
        accessIdentity: session?.user?.id,
    });
    const isCheckingAccess = isPubmed
        ? pubmedPdfAvailability.isChecking
        : unpaywallAccess.isChecking;
    const isFullTextUnavailable = isPubmed
        ? pubmedPdfAvailability.isUnavailable
        : unpaywallAccess.isUnavailable;
    const isPubmedAvailabilityUnknown = isPubmed
        && pubmedPdfAvailability.status === 'unknown';
    const isFullTextAvailabilityUnknown = isPubmedAvailabilityUnknown || (
        !isPubmed
        && !paper.pdf_url
        && unpaywallAccess.isUnknown
    );
    const hasViewOnlyUnpaywallPdf = !isPubmed
        && !paper.pdf_url
        && Boolean(unpaywallAccess.oaPdfUrl);
    const isFullTextBlocked = isPubmed
        ? !pubmedPdfAvailability.canGenerateSummary
        // A browser-only Unpaywall URL is useful for viewing, but the summary
        // worker receives only paper_id and cannot consume an unpersisted URL.
        : !canGenerateSummaryFromPersistedPdf(paper.pdf_url);
    const effectivePdfUrl = isPubmed
        ? pubmedPdfAvailability.effectivePdfUrl
        : paper.pdf_url || unpaywallAccess.oaPdfUrl;
    const metadataAccessStatus: 'open' | 'closed' | 'checking' | 'unavailable' | undefined = isCheckingAccess
        ? 'checking'
        : isFullTextAvailabilityUnknown
            ? undefined
            : isFullTextUnavailable
                ? 'unavailable'
                : 'open';
    const isMobileLayout = width < 640;
    const isAnonymousViewer = pageState.viewer === 'anonymous';
    const isSignupConversion = pageState.primaryCta === 'sign_up';
    const isDigestConversion = pageState.primaryCta === 'create_digest';
    const canRequestSummary = pageState.primaryCta === 'generate_summary'
        || pageState.primaryCta === 'retry_summary';
    const canLoadSummaryEntitlements = canAccessMainApp(session)
        && canRequestSummary;
    const canLoadPreviewEntitlements = canAccessMainApp(session)
        && pageState.viewer === 'owner'
        && Boolean(projectPaperContext?.projectId || digestEntryContext?.digestId);
    const canLoadProjectCreationEntitlements = canAccessMainApp(session)
        && isDigestConversion;
    const entitlementsQuery = useCurrentEntitlements({
        enabled: canLoadSummaryEntitlements
            || canLoadPreviewEntitlements
            || canLoadProjectCreationEntitlements,
    });
    const summaryEntitlementSnapshot = canLoadSummaryEntitlements
        ? entitlementsQuery.data
        : undefined;
    const projectCreationEntitlementSnapshot = canLoadProjectCreationEntitlements
        ? entitlementsQuery.data
        : undefined;
    const isDigestCreationAccessChecking = canLoadProjectCreationEntitlements
        && entitlementsQuery.isFetching
        && !projectCreationEntitlementSnapshot;
    const isDigestCreationAccessUnavailable = canLoadProjectCreationEntitlements
        && entitlementsQuery.isError
        && !projectCreationEntitlementSnapshot;
    const isDigestCreationBlocked = shouldBlockProjectCreation(
        projectCreationEntitlementSnapshot
    );
    const needsProjectCreationTrial = requiresSubscriptionForProjectCreation(projectCreationEntitlementSnapshot);
    const previewClassificationState = {
        isChecking: entitlementsQuery.isFetching,
        isError: entitlementsQuery.isError,
    };
    const projectPreviewAccess = projectPaperContext?.projectId
        ? classifyBillingPreviewProjectAccess(
            entitlementsQuery.data,
            projectPaperContext.projectId,
            previewClassificationState
        )
        : null;
    const digestPreviewAccess = digestEntryContext?.digestId
        ? classifyBillingPreviewDigestAccess(
            entitlementsQuery.data,
            digestEntryContext.digestId,
            previewClassificationState
        )
        : null;
    const isPreviewPaper = canLoadPreviewEntitlements && (
        projectPreviewAccess === 'restricted' || digestPreviewAccess === 'restricted'
    );
    const previewAccessIsChecking = projectPreviewAccess === 'checking'
        || digestPreviewAccess === 'checking';
    const previewAccessIsUnavailable = projectPreviewAccess === 'unavailable'
        || digestPreviewAccess === 'unavailable';
    const isEntitlementAccessChecking = (
        canLoadSummaryEntitlements || canLoadPreviewEntitlements
    ) && (entitlementsQuery.isFetching || previewAccessIsChecking) && !isPreviewPaper;
    const isEntitlementAccessUnavailable = (
        canLoadSummaryEntitlements || canLoadPreviewEntitlements
    ) && (entitlementsQuery.isError || previewAccessIsUnavailable) && !isPreviewPaper;
    const billingPreviewState = getBillingPreviewState(entitlementsQuery.data);
    const previewTrialHref = buildBillingPreviewTrialHref({
        projectId: billingPreviewState.projectId,
        resultDigestId: billingPreviewState.resultDigestId,
    });
    const isSummaryGenerationBlockedBySnapshot = shouldBlockSummaryGeneration(summaryEntitlementSnapshot);
    const legacyAccessEnded = isLegacyAccessEnded(summaryEntitlementSnapshot);
    const legacyUpgradeAction = getLegacyUpgradeAction(summaryEntitlementSnapshot);
    const projectCreationLegacyAccessEnded = isLegacyAccessEnded(
        projectCreationEntitlementSnapshot
    );
    const projectCreationUpgradeAction = getLegacyUpgradeAction(
        projectCreationEntitlementSnapshot
    );
    const summaryLimitErrorMessage = summaryLimitReached ? resolvedSummaryError : null;
    const isSummaryGenerationBlocked = isPreviewPaper
        || isEntitlementAccessChecking
        || isEntitlementAccessUnavailable
        || isSummaryGenerationBlockedBySnapshot
        || Boolean(summaryLimitErrorMessage);
    const isSummaryTrialActivationPrompt = isPreviewPaper || (isSummaryGenerationBlockedBySnapshot
        ? shouldPromptSummaryTrialActivation(summaryEntitlementSnapshot)
        : summaryLimitReached && shouldPromptSummaryTrialActivation(summaryEntitlementSnapshot));
    const summaryLimitMessage = isEntitlementAccessChecking
        ? i18n._(msg({ id: 'summary.access.checkingMessage', message: 'Paperzilla is checking this account before enabling summary actions.' }))
        : isEntitlementAccessUnavailable
            ? i18n._(msg({ id: 'summary.access.unavailableMessage', message: 'Paperzilla could not verify this account. Retry the access check before generating a summary.' }))
            : isSummaryGenerationBlockedBySnapshot || isPreviewPaper
                ? i18n._(buildSummaryLimitMessage(summaryEntitlementSnapshot))
                : summaryLimitErrorMessage;
    const summaryPromptTitle = isEntitlementAccessChecking
        ? i18n._(msg({ id: 'summary.access.checkingTitle', message: 'Checking summary access' }))
        : isEntitlementAccessUnavailable
            ? i18n._(msg({ id: 'summary.access.unavailableTitle', message: 'Could not verify summary access' }))
            : isSummaryTrialActivationPrompt
        ? i18n._(msg({ id: 'summary.access.startTrialTitle', message: 'Start the 14-day trial to create a summary' }))
        : legacyAccessEnded
            ? i18n._(msg({ id: 'summary.access.upgradeTitle', message: 'Upgrade to create summaries' }))
            : isSummaryGenerationBlocked
                ? i18n._(msg({ id: 'summary.access.limitTitle', message: 'AI summary limit reached' }))
                : i18n._(msg({ id: 'summary.empty.title', message: 'No AI summary yet' }));
    const summaryPromptButtonLabel = isEntitlementAccessChecking
        ? i18n._(msg({ id: 'summary.access.checkingAction', message: 'Checking access...' }))
        : isEntitlementAccessUnavailable
            ? i18n._(msg({ id: 'summary.access.retryAction', message: 'Retry access check' }))
            : isSummaryTrialActivationPrompt
        ? i18n._(msg({ id: 'summary.access.activateTrial', message: 'Activate Trial' }))
        : legacyAccessEnded
            ? i18n._(msg({ id: 'summary.access.upgradeAction', message: 'Upgrade to create summaries' }))
            : isSummaryGenerationBlocked
                ? i18n._(msg({ id: 'common.viewPlans', message: 'View plans' }))
                : i18n._(msg({ id: 'summary.generate', message: 'Generate AI Summary' }));
    const feedContextProjectId = pageState.templateProjectId || projectPaperContext?.projectId || null;
    const hasFeedContext = pageState.surface === 'digest' || pageState.hasRecommendationContext;
    const showAnonymousDiscoveryCard = isAnonymousViewer
        && resolvedSummaryState === 'available'
        && !hasFeedContext;
    const showAnonymousTemplateCard = isAnonymousViewer
        && resolvedSummaryState === 'available'
        && hasFeedContext
        && Boolean(feedContextProjectId);
    const showSummaryConversion = isSignupConversion || isDigestConversion;
    const summaryConversionLabel = isSignupConversion
        ? i18n._(msg({ id: 'common.signUp', message: 'Sign Up' }))
        : isDigestCreationAccessChecking
            ? i18n._(msg({ id: 'digest.access.checking', message: 'Checking digest access...' }))
            : isDigestCreationAccessUnavailable
                ? i18n._(msg({ id: 'digest.access.retry', message: 'Retry digest access' }))
                : isDigestCreationBlocked
                    ? i18n._(needsProjectCreationTrial ? projectCreationTrialLabel
                        : msg({ id: 'digest.access.upgradeToCreate', message: 'Upgrade to Create This Digest' }))
                    : feedContextProjectId
                        ? i18n._(msg({ id: 'digest.createMine', message: 'Create My Digest' }))
                        : i18n._(msg({ id: 'digest.createFromPaper', message: 'Create a Digest from This Paper' }));

    const handleBackNavigation = () => {
        if (backNavigationInFlightRef.current) {
            return;
        }

        backNavigationInFlightRef.current = true;
        if (backLinkHref) {
            if (digestEntryContext) {
                router.replace(backLinkHref);
            } else {
                router.push(backLinkHref);
            }
        } else {
            router.back();
        }

        const reset = () => {
            backNavigationInFlightRef.current = false;
        };

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.setTimeout(reset, 800);
            return;
        }

        setTimeout(reset, 800);
    };

    const handleGenerateSummary = () => {
        if (!canAccessMainApp(session)) {
            openAuth();
            return;
        }

        if (isEntitlementAccessChecking) {
            return;
        }

        if (isEntitlementAccessUnavailable) {
            void entitlementsQuery.refreshAuthoritatively().catch((error) => {
                console.error('[PaperDetailView] Failed to refresh summary access:', error);
            });
            return;
        }

        if (isSummaryGenerationBlocked) {
            router.push(
                legacyAccessEnded
                    ? legacyUpgradeAction.path
                    : isPreviewPaper
                        ? previewTrialHref
                        : '/pricing'
            );
            return;
        }

        onGenerateSummary?.();
    };

    const handleViewPlans = () => {
        if (isEntitlementAccessChecking) {
            return;
        }
        if (isEntitlementAccessUnavailable) {
            void entitlementsQuery.refreshAuthoritatively().catch((error) => {
                console.error('[PaperDetailView] Failed to refresh summary access:', error);
            });
            return;
        }
        router.push(
            legacyAccessEnded
                ? legacyUpgradeAction.path
                : isPreviewPaper
                    ? previewTrialHref
                    : '/pricing'
        );
    };

    const handleCreateDigest = () => {
        const useTemplateFlow = Boolean(feedContextProjectId);

        trackFunnelEvent('journey_started', {
            entry_surface: 'paper_page_summary_prompt',
            entry_path: typeof window !== 'undefined'
                ? `${window.location.pathname}${window.location.search}`
                : '/p',
            template_project_id: useTemplateFlow ? feedContextProjectId : null,
            scenario_candidate: useTemplateFlow ? 'template_to_form' : 'chat_to_form',
        });

        const searchParams = new URLSearchParams({
            openChat: 'true',
            entrySurface: 'paper_page_summary_prompt',
        });
        if (useTemplateFlow && feedContextProjectId) {
            searchParams.set('templateProjectId', feedContextProjectId);
        }
        searchParams.set('seedPaperId', paper.id);

        router.push(`/?${searchParams.toString()}`);
    };

    const handleDigestConversionAction = () => {
        if (isDigestCreationAccessChecking) {
            return;
        }

        if (isDigestCreationAccessUnavailable) {
            void entitlementsQuery.refreshAuthoritatively().catch((error) => {
                console.error('[PaperDetailView] Failed to refresh digest access:', error);
            });
            return;
        }

        if (isDigestCreationBlocked) {
            router.push(
                needsProjectCreationTrial ? '/trial' : projectCreationLegacyAccessEnded
                    ? projectCreationUpgradeAction.path
                    : '/pricing'
            );
            return;
        }

        handleCreateDigest();
    };

    const handleSummarySignup = () => {
        openAuth({ mode: 'signup' });
    };

    const handlePrimaryAction = () => {
        if (pageState.primaryCta === 'sign_up') {
            handleSummarySignup();
            return;
        }

        if (pageState.primaryCta === 'create_digest') {
            handleDigestConversionAction();
            return;
        }

        if (pageState.primaryCta === 'retry_summary') {
            if (isSummaryGenerationBlocked) {
                handleViewPlans();
                return;
            }
            onRetry?.();
            return;
        }

        if (pageState.primaryCta === 'generate_summary') {
            handleGenerateSummary();
        }
    };

    const handleJumpToSummary = () => {
        if (!scrollViewRef.current || !aiSummarySectionRef.current) {
            return;
        }

        aiSummarySectionRef.current.measureLayout(
            scrollViewRef.current as any,
            (_x, y) => {
                scrollViewRef.current?.scrollTo({
                    y: Math.max(y - 20, 0),
                    animated: true,
                });
            },
            () => {
                console.error('[PaperDetailView] Failed to measure AI summary section');
            }
        );
    };

    const openVersionedUrl = async (rawUrl: string | null | undefined, label: string) => {
        const resolvedUrl = getVersionedUrl(rawUrl, paper.source_paper_version);
        if (!resolvedUrl) {
            return;
        }

        try {
            const supported = await Linking.canOpenURL(resolvedUrl);
            if (supported) {
                await Linking.openURL(resolvedUrl);
                return;
            }

            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.open(resolvedUrl, '_blank');
                return;
            }

            Alert.alert(
                i18n._(msg({ id: 'common.error', message: 'Error' })),
                i18n._(msg({ id: 'paper.link.cannotOpen', message: `Cannot open ${label}` }))
            );
        } catch (error) {
            console.error(`[PaperDetailView] Failed to open ${label}:`, error);
            Alert.alert(
                i18n._(msg({ id: 'common.error', message: 'Error' })),
                i18n._(msg({ id: 'paper.link.openFailed', message: `Failed to open ${label}` }))
            );
        }
    };

    const primaryAction: PaperActionConfig | null = useMemo(() => {
        if (resolvedSummaryState === 'available') {
            return {
                id: 'ai-summary',
                label: i18n._(msg({ id: 'paper.action.aiSummary', message: 'AI summary' })),
                icon: 'auto-awesome',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: handleJumpToSummary,
                testID: 'paper-primary-cta',
            };
        }

        if (resolvedSummaryState === 'queued' || resolvedSummaryState === 'processing' || resolvedSummaryState === 'loading') {
            return {
                id: 'generating-summary',
                label: i18n._(msg({ id: 'paper.action.generatingSummary', message: 'Generating Summary...' })),
                icon: 'auto-awesome',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: handleJumpToSummary,
                testID: 'paper-primary-cta',
            };
        }

        if (resolvedSummaryState === 'failed') {
            if (showSummaryConversion) {
                return {
                    id: isSignupConversion ? 'summary-sign-up' : 'create-digest',
                    label: summaryConversionLabel,
                    icon: isSignupConversion ? 'person-add' : 'add-circle-outline',
                    iconFamily: 'material',
                    variant: 'primary',
                    onPress: handlePrimaryAction,
                    disabled: isDigestConversion && isDigestCreationAccessChecking,
                    testID: 'paper-primary-cta',
                };
            }

            if (!showSummaryConversion && isCheckingAccess) {
                return {
                    id: 'checking-full-text',
                    label: i18n._(msg({ id: 'paper.action.checkingFullText', message: 'Checking full text...' })),
                    icon: 'hourglass-top',
                    iconFamily: 'material',
                    variant: 'secondary',
                    onPress: handleJumpToSummary,
                    disabled: true,
                    testID: 'paper-primary-cta',
                };
            }

            if (!showSummaryConversion && !isCheckingAccess && isFullTextBlocked) {
                return {
                    id: isFullTextAvailabilityUnknown
                        ? 'full-text-unconfirmed'
                        : hasViewOnlyUnpaywallPdf
                            ? 'summary-pdf-not-prepared'
                            : 'full-text-unavailable',
                    label: isFullTextAvailabilityUnknown
                        ? i18n._(msg({ id: 'paper.action.fullTextUnconfirmed', message: 'Full text not confirmed' }))
                        : hasViewOnlyUnpaywallPdf
                            ? i18n._(msg({ id: 'paper.action.pdfNotPrepared', message: 'PDF not prepared for summary' }))
                            : i18n._(msg({ id: 'paper.action.fullTextUnavailable', message: 'Full text unavailable' })),
                    icon: isFullTextAvailabilityUnknown
                        ? 'help-outline'
                        : hasViewOnlyUnpaywallPdf ? 'info-outline' : 'block',
                    iconFamily: 'material',
                    variant: 'secondary',
                    onPress: handleJumpToSummary,
                    testID: 'paper-primary-cta',
                };
            }

            if (pageState.primaryCta === 'retry_summary') {
                if (isSummaryGenerationBlocked) {
                    return {
                        id: 'summary-limit',
                        label: summaryPromptButtonLabel,
                        icon: 'workspace-premium',
                        iconFamily: 'material',
                        variant: 'primary',
                        onPress: handleViewPlans,
                        disabled: isEntitlementAccessChecking,
                        testID: 'paper-primary-cta',
                    };
                }

                return {
                    id: 'retry-summary',
                    label: i18n._(msg({ id: 'paper.action.retryAiSummary', message: 'Retry AI Summary' })),
                    icon: 'refresh',
                    iconFamily: 'material',
                    variant: 'primary',
                    onPress: handlePrimaryAction,
                    testID: 'paper-primary-cta',
                };
            }

            return {
                id: 'failed-summary',
                label: i18n._(msg({ id: 'paper.action.aiSummaryFailed', message: 'AI Summary Failed' })),
                icon: 'auto-awesome',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: handleJumpToSummary,
                testID: 'paper-primary-cta',
            };
        }

        if (resolvedSummaryState === 'not_requested') {
            if (showSummaryConversion) {
                return {
                    id: isSignupConversion ? 'summary-sign-up' : 'create-digest',
                    label: summaryConversionLabel,
                    icon: isSignupConversion ? 'person-add' : 'add-circle-outline',
                    iconFamily: 'material',
                    variant: 'primary',
                    onPress: handlePrimaryAction,
                    disabled: isDigestConversion && isDigestCreationAccessChecking,
                    testID: 'paper-primary-cta',
                };
            }

            if (!showSummaryConversion && isCheckingAccess) {
                return {
                    id: 'checking-full-text',
                    label: i18n._(msg({ id: 'paper.action.checkingFullText', message: 'Checking full text...' })),
                    icon: 'hourglass-top',
                    iconFamily: 'material',
                    variant: 'secondary',
                    onPress: handleJumpToSummary,
                    disabled: true,
                    testID: 'paper-primary-cta',
                };
            }

            if (!showSummaryConversion && !isCheckingAccess && isFullTextBlocked) {
                return {
                    id: isFullTextAvailabilityUnknown
                        ? 'full-text-unconfirmed'
                        : hasViewOnlyUnpaywallPdf
                            ? 'summary-pdf-not-prepared'
                            : 'full-text-unavailable',
                    label: isFullTextAvailabilityUnknown
                        ? i18n._(msg({ id: 'paper.action.fullTextUnconfirmed', message: 'Full text not confirmed' }))
                        : hasViewOnlyUnpaywallPdf
                            ? i18n._(msg({ id: 'paper.action.pdfNotPrepared', message: 'PDF not prepared for summary' }))
                            : i18n._(msg({ id: 'paper.action.fullTextUnavailable', message: 'Full text unavailable' })),
                    icon: isFullTextAvailabilityUnknown
                        ? 'help-outline'
                        : hasViewOnlyUnpaywallPdf ? 'info-outline' : 'block',
                    iconFamily: 'material',
                    variant: 'secondary',
                    onPress: handleJumpToSummary,
                    testID: 'paper-primary-cta',
                };
            }

            if (pageState.primaryCta !== 'generate_summary') {
                return null;
            }

            if (isSummaryGenerationBlocked) {
                return {
                    id: 'summary-limit',
                    label: summaryPromptButtonLabel,
                    icon: 'workspace-premium',
                    iconFamily: 'material',
                    variant: 'primary',
                    onPress: handleViewPlans,
                    disabled: isEntitlementAccessChecking,
                    testID: 'paper-primary-cta',
                };
            }

            return {
                id: 'generate-summary',
                label: i18n._(msg({ id: 'summary.generate', message: 'Generate AI Summary' })),
                icon: 'auto-awesome',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: handleJumpToSummary,
                testID: 'paper-primary-cta',
            };
        }

        return null;
    }, [handleJumpToSummary, handlePrimaryAction, handleViewPlans, hasViewOnlyUnpaywallPdf, i18n, isCheckingAccess, isDigestConversion, isDigestCreationAccessChecking, isEntitlementAccessChecking, isFullTextAvailabilityUnknown, isFullTextBlocked, isSignupConversion, isSummaryGenerationBlocked, pageState.primaryCta, resolvedSummaryState, showSummaryConversion, summaryConversionLabel, summaryPromptButtonLabel]);

    const secondaryActions: PaperActionConfig[] = useMemo(() => {
        const actions: PaperActionConfig[] = [];

        if (effectivePdfUrl) {
            actions.push({
                id: 'view-pdf',
                label: effectivePdfUrl.includes('chinaxiv.org')
                    ? i18n._(msg({ id: 'paper.action.downloadPdf', message: 'Download PDF' }))
                    : i18n._(msg({ id: 'paper.action.viewPdf', message: 'View PDF' })),
                icon: 'picture-as-pdf',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: () => { void openVersionedUrl(effectivePdfUrl, 'PDF'); },
                testID: 'paper-secondary-pdf',
            });
        }

        if (paper.url) {
            actions.push({
                id: 'view-source',
                label: venueLabel
                    ? i18n._(msg({ id: 'paper.action.viewOnVenue', message: `View on ${venueLabel}` }))
                    : i18n._(msg({ id: 'paper.action.viewSource', message: 'View Source' })),
                icon: 'open-in-new',
                iconFamily: 'material',
                variant: 'secondary',
                onPress: () => { void openVersionedUrl(paper.url, 'source'); },
                testID: 'paper-secondary-source',
            });
        }

        if (isMobileLayout && pageState.canShare && onSharePress) {
            actions.push({
                id: 'share-paper',
                label: i18n._(msg({ id: 'common.share', message: 'Share' })),
                icon: 'share-nodes',
                iconFamily: 'fontawesome',
                variant: 'accent',
                onPress: onSharePress,
                testID: 'paper-secondary-share',
            });
        }

        return actions;
    }, [effectivePdfUrl, i18n, isMobileLayout, onSharePress, pageState.canShare, paper.source_paper_version, paper.url, venueLabel]);

    const feedbackPanel = projectPaperContext && pageState.canFeedback && onSetProjectFeedback && onClearProjectFeedback ? (
        <ProjectPaperFeedbackPanel
            projectPaperContext={projectPaperContext}
            pending={feedbackPending}
            onSetFeedback={onSetProjectFeedback}
            onClearFeedback={onClearProjectFeedback}
        />
    ) : null;
    const hasRecommendationDetails = Boolean(
        projectPaperContext?.recommendationSummary
        || projectPaperContext?.matchingDetails?.explanation
        || projectPaperContext?.matchingDetails?.matchedKeywords?.length
        || projectPaperContext?.matchingDetails?.matchedCategories?.length
        || projectPaperContext?.personalizedNote
    );

    const renderSummarySectionContent = () => {
        // If we already have a summary, show it regardless of access
        if (resolvedSummaryState === 'available' && summary) {
            return (
                <SummaryView
                    paper={paper}
                    summary={summary}
                    showPaperTitle={false}
                    showContents
                />
            );
        }

        // Creating a related digest does not require this paper's PDF. Keep
        // that public conversion path available even when this paper cannot be
        // summarized.
        if (showSummaryConversion && resolvedSummaryState !== 'available') {
            const conversionTitle = isSignupConversion
                ? i18n._(msg({ id: 'paper.conversion.signUpTitle', message: 'Sign up to create your own research digest' }))
                : isDigestCreationAccessChecking
                    ? i18n._(msg({ id: 'paper.conversion.checkingAccessTitle', message: 'Checking digest access' }))
                    : isDigestCreationAccessUnavailable
                        ? i18n._(msg({ id: 'paper.conversion.unavailableTitle', message: 'Could not verify digest access' }))
                        : isDigestCreationBlocked
                            ? i18n._(needsProjectCreationTrial ? projectCreationTrialLabel
                                : msg({ id: 'paper.conversion.limitTitle', message: 'Digest limit reached' }))
                            : feedContextProjectId
                                ? i18n._(msg({ id: 'paper.conversion.createRelatedTitle', message: 'Create your own digest for this research' }))
                                : i18n._(msg({ id: 'paper.conversion.startFromPaperTitle', message: 'Start a research digest from this paper' }));
            const conversionText = isSignupConversion
                ? isFullTextBlocked || isCheckingAccess
                    ? i18n._(msg({ id: 'paper.conversion.signUpLimitedBody', message: 'After signing up, return here to build a research feed around this paper. You can track related work and generate AI summaries for papers with supported full text.' }))
                    : i18n._(msg({ id: 'paper.conversion.signUpBody', message: 'After signing up, return here to build a research feed around this paper. Once the paper is in a digest you own, you can generate its AI summary.' }))
                : isDigestCreationAccessChecking
                    ? i18n._(msg({ id: 'paper.conversion.checkingAccessBody', message: 'Paperzilla is checking your current plan before starting this digest.' }))
                    : isDigestCreationAccessUnavailable
                        ? i18n._(msg({ id: 'paper.conversion.unavailableBody', message: 'Paperzilla could not verify your current plan. Retry the access check before creating this digest.' }))
                        : isDigestCreationBlocked
                            ? i18n._(needsProjectCreationTrial ? projectCreationSubscriptionMessage
                                : msg({ id: 'paper.conversion.limitBody', message: 'Your current plan has reached its digest limit. Upgrade to create a digest for this paper.' }))
                            : feedContextProjectId
                                ? isFullTextBlocked || isCheckingAccess
                                    ? i18n._(msg({ id: 'paper.conversion.relatedLimitedBody', message: 'Use this digest as a starting point. Paperzilla will add this paper to your new feed and rank matching research. AI summaries are available for feed papers with supported full text.' }))
                                    : i18n._(msg({ id: 'paper.conversion.relatedBody', message: 'Use this digest as a starting point. Paperzilla will add this paper to your new feed and rank matching research, where you can generate AI summaries.' }))
                                : isFullTextBlocked || isCheckingAccess
                                    ? i18n._(msg({ id: 'paper.conversion.newLimitedBody', message: 'Paperzilla will prefill a research topic from this paper and add it as the starting paper in your new feed. You can track related work and generate AI summaries for papers with supported full text.' }))
                                    : i18n._(msg({ id: 'paper.conversion.newBody', message: 'Paperzilla will prefill a research topic from this paper and add it as the starting paper in your new feed. There you can track related work and generate AI summaries.' }));

            return (
                <View style={styles.summaryPromptCard}>
                    <MaterialIcons name="auto-awesome" size={40} color={colors.primaryGreen} />
                    <Text style={[styles.summaryPromptTitle, fontsLoaded && interSemiBold]}>
                        {conversionTitle}
                    </Text>
                    <Text style={[styles.summaryPromptText, fontsLoaded && interRegular]}>
                        {conversionText}
                    </Text>
                    <Button
                        label={summaryConversionLabel}
                        onPress={isSignupConversion
                            ? handleSummarySignup
                            : handleDigestConversionAction}
                        variant="primary"
                        testID="paper-summary-conversion-cta"
                        disabled={isDigestConversion && isDigestCreationAccessChecking}
                    />
                </View>
            );
        }

        // Checking backend-owned PubMed availability or Unpaywall for other sources.
        if (isCheckingAccess) {
            return (
                <View style={styles.summaryPromptCard}>
                    {isPubmed ? (
                        <MaterialIcons name="hourglass-top" size={40} color={colors.text500} />
                    ) : (
                        <ActivityIndicator size="small" color={colors.text500} />
                    )}
                    {isPubmed ? (
                        <Text style={[styles.summaryPromptTitle, fontsLoaded && interSemiBold]}><Trans id="papers.paperdetailview.checkingFullTextAvailability" comment="UI copy in components/papers/PaperDetailView.tsx">
                            Checking full-text availability
                        </Trans></Text>
                    ) : null}
                    <Text style={[styles.summaryPromptText, fontsLoaded && interRegular]}>
                        {isPubmed
                            ? <Trans id="papers.paperdetailview.paperzillaIsCheckingTrustedOpenAccessSourcesSummaryActionsWillAppear" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Paperzilla is checking trusted open-access sources. Summary actions will appear when a PDF can be obtained.</Trans>
                            : <Trans id="papers.paperdetailview.checkingAccess" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Checking access...</Trans>}
                    </Text>
                </View>
            );
        }

        // No usable PDF is available, so a new summary cannot be generated.
        if (isFullTextBlocked && resolvedSummaryState !== 'queued' && resolvedSummaryState !== 'processing' && resolvedSummaryState !== 'loading') {
            return (
                <View style={styles.summaryPromptCard}>
                    <MaterialIcons
                        name={isFullTextAvailabilityUnknown
                            ? 'help-outline'
                            : hasViewOnlyUnpaywallPdf ? 'info-outline' : 'block'}
                        size={40}
                        color="#92400e"
                    />
                    <Text style={[styles.summaryPromptTitle, fontsLoaded && interSemiBold]}>
                        {isFullTextAvailabilityUnknown
                            ? <Trans id="papers.paperdetailview.fullTextAvailabilityNotConfirmed" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Full-text availability not confirmed</Trans>
                            : hasViewOnlyUnpaywallPdf
                                ? <Trans id="papers.paperdetailview.pdfLinkAvailableButNotPreparedForAiSummary" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">PDF link available, but not prepared for AI summary</Trans>
                                : <Trans id="papers.paperdetailview.fullTextUnavailableForAiSummary" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Full text unavailable for AI summary</Trans>}
                    </Text>
                    <Text style={[styles.summaryPromptText, styles.summaryPromptTextWide, fontsLoaded && interRegular]}>
                        {isFullTextAvailabilityUnknown
                            ? <Trans id="papers.paperdetailview.paperzillaHasNotConfirmedADownloadableFullTextPdfYetReload" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Paperzilla has not confirmed a downloadable full-text PDF yet. Reload this page later to check again.</Trans>
                            : hasViewOnlyUnpaywallPdf
                                ? <Trans id="papers.paperdetailview.anExternalOpenAccessPdfLinkIsAvailableToViewBut" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">An external open-access PDF link is available to view, but Paperzilla has not verified and stored it for summary generation.</Trans>
                                : isPubmed
                                ? <Trans id="papers.paperdetailview.paperzillaCouldNotFindADownloadableFullTextPdfFromIts" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Paperzilla could not find a downloadable full-text PDF from its supported sources. You can still view the PubMed record.</Trans>
                                : <Trans id="papers.paperdetailview.paperzillaDoesNotHaveADownloadableFullTextPdfPreparedFor" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Paperzilla does not have a downloadable full-text PDF prepared for AI summary generation.</Trans>}
                    </Text>
                    <Text style={[styles.summaryPromptText, styles.summaryPromptTextWide, fontsLoaded && interRegular]}><Trans id="papers.paperdetailview.ifYouHaveACopyOfThePdfSendIt" comment="UI copy in components/papers/PaperDetailView.tsx">
                        If you have a copy of the PDF, send it to hello@paperzilla.ai and we'll summarize it for you.
                    </Trans></Text>
                </View>
            );
        }

        if (resolvedSummaryState === 'queued' || resolvedSummaryState === 'processing') {
            return <SummaryStatusIndicator status={resolvedSummaryState} job={summaryJob} />;
        }

        if (resolvedSummaryState === 'failed') {
            return (
                <SummaryStatusIndicator
                    status="failed"
                    job={summaryJob}
                    error={summaryLimitMessage || summaryJob?.error_message}
                    onRetry={pageState.primaryCta === 'retry_summary' && !isSummaryGenerationBlocked
                        ? onRetry
                        : undefined}
                />
            );
        }

        if (resolvedSummaryState === 'loading') {
            return <SummaryStatusIndicator status="generating" />;
        }

        if (!canRequestSummary) {
            return (
                <View style={styles.summaryPromptCard}>
                    <MaterialIcons name="lock-outline" size={40} color={colors.text500} />
                    <Text style={[styles.summaryPromptTitle, fontsLoaded && interSemiBold]}><Trans id="papers.paperdetailview.aiSummaryUnavailableHere" comment="UI copy in components/papers/PaperDetailView.tsx">
                        AI summary unavailable here
                    </Trans></Text>
                    <Text style={[styles.summaryPromptText, fontsLoaded && interRegular]}><Trans id="papers.paperdetailview.aiSummariesCanBeCreatedForEligibleRecommendedPapersIn" comment="UI copy in components/papers/PaperDetailView.tsx">
                        AI summaries can be created for eligible recommended papers in a research digest you own.
                    </Trans></Text>
                </View>
            );
        }

        return (
            <View style={styles.summaryPromptCard}>
                <MaterialIcons
                    name={isSummaryGenerationBlocked ? 'info' : 'auto-awesome'}
                    size={40}
                    color={isSummaryGenerationBlocked ? colors.blue700 : colors.primaryGreen}
                />
                <Text style={[styles.summaryPromptTitle, fontsLoaded && interSemiBold]}>
                    {summaryPromptTitle}
                </Text>
                <Text style={[styles.summaryPromptText, fontsLoaded && interRegular]}>
                    {summaryLimitMessage || (pageState.surface === 'standalone'
                        ? <Trans id="papers.paperdetailview.generateAStructuredSummaryWithMethodologyResultsLimitationsAndTakeaway" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">Generate a structured summary with methodology, results, limitations, and takeaway without leaving the paper page.</Trans>
                        : <Trans id="papers.paperdetailview.theFullAiSummaryWillAppearHereOnceItIs" comment="Conditional UI copy in components/papers/PaperDetailView.tsx">The full AI summary will appear here once it is generated for this paper.</Trans>)}
                </Text>
                {resolvedSummaryError && !summaryLimitMessage ? (
                    <Text style={[styles.summaryPromptError, fontsLoaded && interRegular]}>
                        {resolvedSummaryError}
                    </Text>
                ) : null}
                <Button
                    label={summaryPromptButtonLabel}
                    onPress={isSummaryGenerationBlocked ? handleViewPlans : handleGenerateSummary}
                    variant="primary"
                    disabled={isEntitlementAccessChecking}
                    testID={isSummaryGenerationBlocked ? 'paper-summary-view-plans' : 'paper-summary-generate'}
                />
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <Header hidePreviewTrialBanner={isPreviewPaper} />
            <ScrollView
                ref={scrollViewRef}
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.contentWrapper}>
                    <View style={styles.pageGrid}>
                        <View style={styles.mainColumn}>
                            {showBackLink && !digestEntryContext ? (
                                <TouchableOpacity
                                    style={styles.backLink}
                                    onPress={handleBackNavigation}
                                    activeOpacity={0.72}
                                    testID="paper-back-link"
                                >
                                    <Text style={[styles.backLinkText, fontsLoaded && interSemiBold]}>
                                        {backLinkText}
                                    </Text>
                                </TouchableOpacity>
                            ) : null}

                            <View style={styles.heroCard}>
                                {digestEntryContext && projectPaperContext ? (
                                    <DigestContextBanner
                                        digestEntryContext={digestEntryContext}
                                        projectPaperContext={projectPaperContext}
                                    />
                                ) : null}

                                <View style={styles.heroHeaderRow}>
                                    <View style={styles.heroMetadataWrap}>
                                        <PaperMetadata paper={paper} projectPaperContext={projectPaperContext} accessStatus={metadataAccessStatus} />
                                    </View>

                                    {!isMobileLayout && pageState.canShare && onSharePress ? (
                                        <Button
                                            label={msg({ id: "papers.paperdetailview.share", message: "Share", comment: "UI label in components/papers/PaperDetailView.tsx" })}
                                            onPress={onSharePress}
                                            variant="secondary"
                                            iconName="share-nodes"
                                            iconColor="#ffffff"
                                            style={styles.heroShareButton}
                                            textStyle={styles.heroShareButtonText}
                                            testID="paper-secondary-share"
                                        />
                                    ) : null}
                                </View>

                                <View style={styles.heroActions}>
                                    <PaperActions
                                        primaryAction={primaryAction}
                                        secondaryActions={secondaryActions}
                                    />
                                </View>

                                <PaperCliPromo
                                    paper={paper}
                                    projectPaperContext={projectPaperContext}
                                    canUseRecommendationCommand={pageState.viewer === 'owner'}
                                    variant={pageState.surface === 'digest' ? 'inline' : 'spotlight'}
                                />
                            </View>

                            {isPreviewPaper ? (
                                <PreviewTrackingCard
                                    projectName={projectPaperContext?.projectName}
                                    previewProjectId={billingPreviewState.projectId}
                                    resultDigestId={billingPreviewState.resultDigestId}
                                    trackingState={billingPreviewState.isPaused ? 'paused' : 'pausing'}
                                    style={styles.previewTrackingCard}
                                />
                            ) : null}

                            {!showSummaryConversion && showAnonymousDiscoveryCard ? (
                                <CreateFeedLikeThisCard
                                    projectId={null}
                                    entrySurface="paper_page_anonymous_card"
                                    title={msg({ id: "papers.paperdetailview.trackNewPapersLikeThisAutomatically", message: "Track New Papers Like This Automatically", comment: "UI title in components/papers/PaperDetailView.tsx" })}
                                    description={msg({ id: "papers.paperdetailview.createAPersonalizedTopicFeedAndLetPaperzillaBringYouRelevantNewPapersAndDigestEm", message: "Create a personalized topic feed and let Paperzilla bring you relevant new papers and digest emails for your research interests.", comment: "UI description in components/papers/PaperDetailView.tsx" })}
                                    buttonLabel={msg({ id: "papers.paperdetailview.trackPapersLikeThis", message: "Track Papers Like This", comment: "UI buttonLabel in components/papers/PaperDetailView.tsx" })}
                                    useTemplate={false}
                                    style={styles.templateCard}
                                />
                            ) : !showSummaryConversion && showAnonymousTemplateCard ? (
                                <CreateFeedLikeThisCard
                                    projectId={feedContextProjectId}
                                    entrySurface="paper_page_template_card"
                                    useTemplate={true}
                                    style={styles.templateCard}
                                />
                            ) : null}

                            <View
                                nativeID="paper-section-abstract"
                                style={styles.sectionCard}
                                testID="paper-section-abstract"
                            >
                                <PaperAbstract abstract={paper.abstract} />

                                {feedbackPanel ? (
                                    <View style={styles.abstractFeedbackWrap}>
                                        {feedbackPanel}
                                    </View>
                                ) : null}
                            </View>

                            {pageState.hasRecommendationContext && projectPaperContext && hasRecommendationDetails ? (
                                <View
                                    nativeID="paper-section-why-matched"
                                    style={styles.sectionCard}
                                    testID="paper-section-why-matched"
                                >
                                    <PaperDigestContext projectPaperContext={projectPaperContext} />
                                </View>
                            ) : null}

                            <View
                                nativeID="paper-section-ai-summary"
                                ref={aiSummarySectionRef}
                                style={styles.sectionCard}
                                testID="paper-section-ai-summary"
                            >
                                <Text style={[styles.sectionKicker, fontsLoaded && interSemiBold]}><Trans id="papers.paperdetailview.aiSummary" comment="UI copy in components/papers/PaperDetailView.tsx">
                                    AI Summary
                                </Trans></Text>
                                {renderSummarySectionContent()}
                            </View>
                        </View>
                    </View>
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    previewTrackingCard: {
        marginBottom: 20,
    },
    contentWrapper: {
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: 24,
        paddingBottom: 44,
    },
    pageGrid: {
        width: '100%',
        maxWidth: 960,
    },
    mainColumn: {
        flex: 1,
        gap: 20,
    },
    backLink: {
        alignSelf: 'flex-start',
    },
    backLinkText: {
        fontSize: 14,
        color: colors.primaryGreenDark,
    },
    heroCard: {
        padding: 26,
        borderRadius: 24,
        backgroundColor: '#fbfcfe',
        borderWidth: 1,
        borderColor: '#dde5ef',
        gap: 20,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOpacity: 0.06,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 6 },
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0 18px 48px rgba(11, 16, 32, 0.06)',
        }),
    },
    heroHeaderRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
    },
    heroMetadataWrap: {
        flex: 1,
        minWidth: 0,
    },
    heroShareButton: {
        backgroundColor: colors.action,
        borderColor: colors.action,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 999,
        alignSelf: 'flex-start',
        ...(Platform.OS === 'web' && {
            boxShadow: 'none',
        }),
    },
    heroShareButtonText: {
        color: '#ffffff',
        fontWeight: '600',
    },
    heroActions: {
        paddingTop: 4,
    },
    templateCard: {
        marginBottom: 0,
    },
    sectionCard: {
        borderRadius: 22,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.border,
        padding: 22,
        gap: 14,
    },
    sectionKicker: {
        fontSize: 12,
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.45,
    },
    abstractFeedbackWrap: {
        marginTop: 4,
        alignItems: 'center',
    },
    summaryPromptCard: {
        alignItems: 'center',
        gap: 14,
        paddingVertical: 12,
    },
    summaryPromptTitle: {
        fontSize: 22,
        color: colors.text900,
        textAlign: 'center',
    },
    summaryPromptText: {
        maxWidth: 620,
        fontSize: 15,
        lineHeight: 24,
        color: colors.text700,
        textAlign: 'center',
    },
    summaryPromptTextWide: {
        maxWidth: 780,
    },
    summaryPromptError: {
        maxWidth: 620,
        fontSize: 14,
        lineHeight: 21,
        color: colors.errorRed,
        textAlign: 'center',
    },
});
